import { useMemo } from 'react';
import {
  dimensionPictures, dimensionsPrompt, getDimsThinking,
} from './dimensionsRun';
import { CallBadge } from './CallBadge';
import { reasoningTooltip } from './ReasoningBadge';
import { usePartsRun, type PartItem } from './partsRun';
import { useListing, useSubmodels } from './listingStore';
import { useLanguage } from '../../contexts/LanguageContext';

// Read every part's real size off the listing's dimension drawing — and the whole product's,
// in the same call, which is what anchors the assembled product's mesh in Phase 5.
//
// It sits in Phase 2, under the parts table, because the table is what it fills in: the
// SIZE row shows the manual's printed spec for hardware and nothing at all for the pieces
// the furniture is actually made of, and this is what puts a number in those cells.
//
// It has no button of its own any more. Listing the parts and sizing them are one action
// now — you never want the list without the sizes — so the phase's run button does both,
// and what is left here is everything that action has to SAY about its size half: what it
// will read from, what it read, the prompt it sent, and what the model thought.
//
// It still says the absence out loud. A run that silently did nothing because the listing
// had no dimension drawing is indistinguishable from one that found no measurements: the
// badge is greyed then, and clicking it says so.
export function DimensionsInfo({ docId, items, onRun, running, disabled, error }: {
  docId: string;
  // The parts this is about, handed in rather than read live. A fresh extraction empties the
  // store's list before it fills it again (see extractParts), so a live read answers "no
  // parts to measure" for the whole of the call that is about to produce them — and this
  // badge disappeared from beside the button on the one press that makes it worth reading.
  // PartsPhase keeps the last list this document had.
  items: PartItem[];
  // THE SIZE READ ON ITS OWN — without the list, the crops and the materials the phase's
  // button makes around it. Re-reading a drawing is a question about sizes, and the button
  // was the only way to ask it: four paid calls and a re-listed table for one of them.
  //
  // Held by PartsPhase, which is where the table's cells learn a size read is out (`sizing`);
  // this badge only offers it.
  onRun: () => void;
  running: boolean;
  // Something else is in flight — the whole press — or the phase is locked.
  disabled: boolean;
  // Why the last read from here failed, or ''.
  error: string;
}) {
  const { t } = useLanguage();
  const { busy } = usePartsRun(docId);
  const listing = useListing(docId);
  // The dimension pictures come from HERE, so the value is used and not merely
  // subscribed to. Subscribing alone re-rendered this but left the memo below on its
  // cached answer, and the classification lands after the first render — so the flag
  // could be set, correctly, on the right picture, and this still said "no drawing".
  const submodels = useSubmodels(docId);

  // Keyed on the submodels and not on the parts: which pictures carry measurements is a
  // fact about the LISTING, and `items` was simply the wrong thing to watch.
  const pictures = useMemo(() => dimensionPictures(docId), [docId, submodels]);
  // Read on every render rather than nudged by a counter: the run that writes it is the
  // phase's now, not this component's, so there is no moment here to bump one at.
  const thinking = getDimsThinking(docId);

  // Any list at all: the product's overall size is asked whatever the parts are, so a list of
  // nothing but hardware still has a question to show.
  // DRAWN EVEN WITH NOTHING TO ASK ABOUT, greyed out: the phase shows its whole sequence.
  const nothingToAsk = !items.length;

  // A row, not a block. It hangs off the side of the run button (see PartsPhase) rather
  // than sitting under it, so it must be exactly as tall as the badge in it and no wider
  // than that needs: it is positioned against the button, and a full-width box there would
  // be a full-width box pinned to one edge of it.
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      {error && (
        <span title={error} className="max-w-[18rem] truncate text-[11px] text-rose-400">
          {error}
        </span>
      )}

      {/* ONE BADGE FOR ONE CALL, as everywhere else in the app. This was two: a document
          to hover for the prompt and a brain to hover for the answer — the two halves of
          a single size read, drawn as two features, the second of which only appeared
          once a run had happened, so the row changed shape under the pointer. See
          CallBadge, where the argument is written out and the reading window lives.

          The drawings are no longer laid out here either. They are the pictures of the
          call, so PromptPanel draws them above its text, zoomable — which is also what
          took the lightbox and its zoom state out of this file. */}
      <CallBadge
        title={t('notice.parts.badgeSize')}
        named
        // NOTHING TO READ WITHOUT A DRAWING, and the absence is said on a click (Louis
        // 2026-11-03): a run that silently did nothing because the listing had no dimension
        // drawing is indistinguishable from one that found no measurements. It was a line of
        // text beside the badge; the badge is greyed now, and says it when clicked.
        unavailable={pictures.length ? undefined : t('notice.parts.dimsNoDrawing')}
        run={{
          onRun,
          busy: running,
          disabled: disabled || nothingToAsk || !pictures.length,
        }}
        promptLabel={t('notice.parts.dimsPrompt')}
        load={() => dimensionsPrompt(docId, listing.productName)}
        // Absent until there has been a read, and the badge is then a menu of one: it
        // opens the prompt directly rather than offering a choice with nothing behind it.
        answer={thinking && !busy ? {
          label: t('notice.parts.dimsThinking'),
          content: reasoningTooltip(thinking.reasoning, thinking.raw, t),
        } : undefined}
      />
    </div>
  );
}
